import React, {useEffect, useState} from "react";
import axios from "axios";
import {AlertTriangle, RotateCw} from "lucide-react";

import * as Constants from "../../utils/Constants";
import * as UrlConstants from "../../utils/UrlConstants";
import {getAuthToken, getClientIdOfUser, showToast, formatDateTime} from "../../utils/utility";
import {sessionStatusMeta, executionStatusMeta, extractErrorMessage} from "./scheduledUnlockMeta";
import useScheduledUnlockUpdates from "../../hooks/useScheduledUnlockUpdates";

import SlideOver from "../../components/ui/SlideOver";
import Button from "../../components/ui/Button";
import Pill from "../../components/ui/Pill";
import Spinner from "../../components/ui/Spinner";
import Modal from "../../components/ui/Modal";

/**
 * Slide-over: full detail of one scheduled-unlock session, including the
 * per-lock execution audit trail (attempts, retries, ack/failure detail).
 */
export default function ScheduledUnlockDetailSlideOver({sessionId, open, onClose, onChanged, onEdit}) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const fetchDetail = async () => {
    if (!sessionId) return;
    try {
      setLoading(true);
      const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/${UrlConstants.SCHEDULED_UNLOCK_DETAIL(sessionId)}`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const res = await axios.get(url);
      setSession(res.data);
    } catch (err) {
      showToast(extractErrorMessage(err, "Could not load session detail"), {type: "error"});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sessionId]);

  // Live progress while the panel is open — replaces the on-open snapshot with each broadcast
  // for this session (attempt outcomes, retries, final status), no polling/refetching needed.
  useScheduledUnlockUpdates((data) => {
    if (data.id === sessionId) setSession(data);
  }, open);

  const doCancel = async () => {
    try {
      setCancelling(true);
      const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/${UrlConstants.SCHEDULED_UNLOCK_CANCEL(sessionId)}`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const res = await axios.post(url);
      setSession((prev) => ({...prev, ...res.data}));
      showToast("Scheduled unlock cancelled", {type: "success"});
      onChanged && onChanged();
    } catch (err) {
      showToast(extractErrorMessage(err, "Could not cancel this session"), {type: "error"});
    } finally {
      setCancelling(false);
      setConfirmCancel(false);
    }
  };

  const statusMeta = session ? sessionStatusMeta(session.status) : null;

  return (
    <>
      <SlideOver
        open={open}
        onOpenChange={(o) => !o && onClose && onClose()}
        title={session?.sessionName || "Scheduled unlock"}
        subtitle={session ? `${session.totalLockCount} lock(s) · created by ${session.createdByUsername}` : undefined}
        size="lg"
      >
        <SlideOver.Body>
          {loading || !session ? (
            <div className="py-12 flex items-center justify-center gap-3 text-ink-500">
              <Spinner size="md" /><span className="text-sm">Loading…</span>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <Field label="Status">
                  <Pill tone={statusMeta.tone}>{statusMeta.label}</Pill>
                </Field>
                <Field label="Progress">
                  <span className="font-medium text-ink-900">
                    {session.successCount} succeeded, {session.failedCount} failed / {session.totalLockCount}
                  </span>
                </Field>
                <Field label="Scheduled for">
                  <span>{formatDateTime(session.scheduledAtUtc) || "—"}</span>
                  <div className="text-xs text-ink-500">
                    Requested {session.requestedLocalTime} ({session.requestedTimezone})
                  </div>
                </Field>
                <Field label="Started">{formatDateTime(session.startedAt) || "—"}</Field>
                <Field label="Completed">{formatDateTime(session.completedAt) || "—"}</Field>
                <Field label="Created">{formatDateTime(session.createdAt) || "—"}</Field>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-ink-700 mb-2">Locks in this batch</h3>
                <div className="border border-ink-200 rounded-md overflow-hidden divide-y divide-ink-100">
                  {(session.executions || []).map((ex) => {
                    const exMeta = executionStatusMeta(ex.status);
                    return (
                      <div key={ex.id} className="px-4 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-medium text-ink-900 font-mono truncate">{ex.deviceId}</div>
                            <div className="text-xs text-ink-500">
                              Attempt {ex.attemptCount}/{ex.maxAttempts}
                              {ex.lastAttemptedAt && <> · last {formatDateTime(ex.lastAttemptedAt)}</>}
                            </div>
                          </div>
                          <Pill tone={exMeta.tone} size="sm">{exMeta.label}</Pill>
                        </div>
                        {ex.status === "RETRYING" && ex.nextRetryAt && (
                          <div className="mt-1.5 flex items-center gap-1.5 text-xs text-warning-700">
                            <RotateCw className="h-3.5 w-3.5" />
                            Next retry {formatDateTime(ex.nextRetryAt)}
                          </div>
                        )}
                        {ex.status === "FAILED" && ex.failureReason && (
                          <div className="mt-1.5 flex items-start gap-1.5 text-xs text-danger-700">
                            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                            {ex.failureReason}
                          </div>
                        )}
                        {ex.status === "SUCCESS" && ex.ackDetails && (
                          <div className="mt-1.5 text-xs text-ink-500">{ex.ackDetails}</div>
                        )}
                      </div>
                    );
                  })}
                  {(session.executions || []).length === 0 && (
                    <div className="px-4 py-6 text-center text-sm text-ink-500">No locks recorded.</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </SlideOver.Body>
        <SlideOver.Footer>
          <Button variant="secondary" onClick={onClose}>Close</Button>
          {session?.status === "SCHEDULED" && (
            <>
              <Button variant="secondary" onClick={() => onEdit && onEdit(session)}>
                Edit
              </Button>
              <Button variant="danger" onClick={() => setConfirmCancel(true)} loading={cancelling}>
                Cancel session
              </Button>
            </>
          )}
        </SlideOver.Footer>
      </SlideOver>

      <Modal
        open={confirmCancel}
        onOpenChange={setConfirmCancel}
        title="Cancel scheduled unlock?"
        subtitle="The locks in this batch will not be unlocked."
        size="sm"
      >
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmCancel(false)} disabled={cancelling}>
            Keep it
          </Button>
          <Button variant="danger" onClick={doCancel} loading={cancelling}>
            Cancel session
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

function Field({label, children}) {
  return (
    <div>
      <div className="text-xs font-medium text-ink-500 uppercase tracking-wide">{label}</div>
      <div className="mt-0.5 text-sm text-ink-900">{children}</div>
    </div>
  );
}
